import { getSettingMultimodalProductSearch } from "@faire/retailer-visitor-shared/settings/useSettingMultimodalProductSearch";

/** Encodes a text and images url into a single string to represent a multimodal query */
export const encodeMultimodalQuery = (
  text: string,
  imageUrls: string[]
): string => {
  if (!getSettingMultimodalProductSearch()) {
    return text;
  }

  return `||text||${text}||image||${imageUrls.join(",")}`;
};

/**
 * Decodes a multimodal query string into a text and images url.
 * Returns undefined if the query is not a multimodal query or if the feature is disabled.
 */
export const decodeMultimodalQuery = (
  query: string | undefined
): { text: string; imageUrls: string[] } | undefined => {
  if (!getSettingMultimodalProductSearch()) {
    return undefined;
  }
  if (query === undefined) {
    return undefined;
  }

  const pattern = /^\|\|text\|\|(.*)\|\|image\|\|(.*)$/;
  const match = query.match(pattern);
  if (match) {
    const [, text, imageUrls] = match;
    return {
      text: text ?? "",
      imageUrls: (imageUrls ?? "").split(",").filter(Boolean),
    };
  } else {
    return undefined;
  }
};

// Helper type to ensure that the result type is a string if the input type is a string.
type DecodeMultimodalQueryTextResultType<T extends string | undefined> =
  T extends undefined ? string | undefined : string;

/**
 * Decodes a multimodal query string into a text, returning only the text part.
 * Returns the query if it is not a multimodal query or if the feature is disabled.
 */
export function decodeMultimodalQueryText<T extends string | undefined>(
  query: T
): DecodeMultimodalQueryTextResultType<T> {
  return (
    decodeMultimodalQuery(query)?.text ??
    (query as DecodeMultimodalQueryTextResultType<T>)
  );
}
